import { DotsEditCustom } from '@repo/go-icons';
import { ConfigContext, Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GoStorageObject } from '../../../shared/api/IDashboardService';
import { ActionList } from '../../../shared/components/ActionList';
import type { Actions } from '../../../shared/components/ActionList/ActionList';
import styles from '../AlertRulesTable.module.css';
import { openDeleteTemplateModal } from '../components/openDeleteTemplateModel';
import { openEditTemplateModal } from '../components/openEditTemplateModal';
import type { AlertRulesService } from '../services/AlertRulesService';
import type { AlertRulesTemplatesManager, TAlertRulesTemplateData } from '../services/AlertRulesTemplatesManager';

interface Props {
  template: GoStorageObject<TAlertRulesTemplateData>;
  templateManager: AlertRulesTemplatesManager;
  service: AlertRulesService;
}

export const TemplateItem = observer(({ service, template, templateManager }: Props) => {
  const configProvider = useContext(ConfigContext);
  const { t, i18n } = useTranslation();
  const [actionsOpen, setActionsOpen] = useState(false);
  const isPersonal = template.data.ownerUserId != null;

  const actions: Actions = [];
  actions.push({
    id: 0,
    title: t('alertsManagerWidget:alertRules.templatesDrawer.addToTable'),
    onClick: () => {
      service.addFromTemplate(template.data);
      setActionsOpen(false);
    },
    disabled: !service.uiState.editing,
  });
  actions.push({
    id: 1,
    title: t('alertsManagerWidget:alertRules.templatesDrawer.edit'),
    onClick: () => {
      openEditTemplateModal(template, templateManager, configProvider, i18n);
      setActionsOpen(false);
    },
  });
  if (isPersonal) {
    actions.push({
      id: 2,
      title: t('alertsManagerWidget:alertRules.templatesDrawer.duplicateToPublic'),
      onClick: () => {
        templateManager.duplicateTemplateToPublic(template.id);
        setActionsOpen(false);
      },
    });
  } else {
    actions.push({
      id: 3,
      title: t('alertsManagerWidget:alertRules.templatesDrawer.duplicateToPersonal'),
      onClick: () => {
        templateManager.duplicateTemplateToPersonal(template.id);
        setActionsOpen(false);
      },
    });
  }
  actions.push({
    id: 4,
    title: t('alertsManagerWidget:alertRules.templatesDrawer.delete'),
    onClick: () => {
      openDeleteTemplateModal(template, templateManager, configProvider, i18n);
      setActionsOpen(false);
    },
    variant: 'dangerous',
  });

  return (
    <div className={clsx(styles.templateItemContainer)}>
      <div>{template.data.name}</div>
      <Tooltip
        placement="bottom-end"
        title={<ActionList actions={actions} />}
        trigger={['click']}
        open={actionsOpen}
        onOpenChange={setActionsOpen}
        style={{ paddingLeft: 0, paddingRight: 0 }}
        withArrow={false}
      >
        <div className={styles.tooltipIcon}>
          <DotsEditCustom />
        </div>
      </Tooltip>
    </div>
  );
});
